import { Button, Form } from '@heroui/react'
import { use2FAManagement } from '@qse/auth-ui'
import { type ModalComponentProps, validateVerificationCode } from '@qse/constants'
import { useUserStore } from '@qse/stores'
import { HeroInput } from '@qse/ui'
import { QRCodeSVG } from 'qrcode.react'
import { useEffect, useRef, useState } from 'react'
import React from 'react'
import { toast } from 'sonner'

export default function TwoFASetup(props: ModalComponentProps) {
  const { setModelActive, onClose } = props
  const { setup2FA, verify2FASetup, isLoading } = use2FAManagement()
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('')
  const [secretCode, setSecretCode] = useState<string>('')
  const [isInitializing, setIsInitializing] = useState(false)
  const [showSecretCode, setShowSecretCode] = useState(false)
  const { updateMfaStatus } = useUserStore()

  const hasInitialized = useRef(false)

  useEffect(() => {
    if (hasInitialized.current) return

    const initializeSetup = async () => {
      if (isInitializing) return

      try {
        setIsInitializing(true)
        hasInitialized.current = true
        const totpSetup = await setup2FA()

        if (totpSetup) {
          setQrCodeUrl(totpSetup.qrCodeUrl)
          setSecretCode(totpSetup.secretCode)
        }
      } catch (error) {
        console.error('Error initializing 2FA setup:', error)
      } finally {
        setIsInitializing(false)
      }
    }

    initializeSetup()
  }, [setup2FA, isInitializing])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.target as HTMLFormElement)
    const values = Object.fromEntries(formData)
    const code = values.code as string

    try {
      const success = await verify2FASetup(code)

      if (success) {
        updateMfaStatus('enabled')
        onClose()
      }
    } catch (error) {
      console.error('Error verifying 2FA setup:', error)
    }
  }

  const handleCancel = () => {
    setModelActive(undefined)
    onClose()
  }

  return (
    <div>
      <Form
        className="flex flex-col gap-5 py-5 px-8 w-full"
        validationBehavior="native"
        onSubmit={handleSubmit}
      >
        <div className="text-sm text-gray-900 dark:text-white text-center max-w-md">
          <p className="mb-2 font-medium text-lg">Authenticator App Setup</p>
          <ol className="list-decimal list-inside space-y-1 text-left">
            <li>
              Install an authenticator app on your mobile device. Popular ones include Authy, Google
              Authenticator or the Microsoft Authenticator
            </li>
            <li>Look for an “Add an account” button</li>
            <li>When requested to do so, scan the barcode below</li>
          </ol>
        </div>
        {isInitializing ? (
          <div className="flex flex-col gap-4 items-center w-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            <p className="text-sm text-gray-600 dark:text-gray-400">Generating QR code...</p>
          </div>
        ) : qrCodeUrl ? (
          <div className="flex flex-col gap-4 items-center w-full">
            <div className="bg-white p-4 rounded-lg">
              <QRCodeSVG size={200} value={qrCodeUrl} />
            </div>

            <div className="text-center">
              <button
                className="text-blue-500 hover:text-blue-600 text-sm underline cursor-pointer"
                type="button"
                onClick={() => setShowSecretCode(!showSecretCode)}
              >
                Can't scan it?
              </button>

              {showSecretCode && (
                <div className="mt-3">
                  <p className="text-sm font-medium mb-2 text-gray-900 dark:text-gray-300">
                    Manual Entry Code
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-lg font-mono text-sm break-all text-gray-900 dark:text-gray-200 flex-1">
                      {secretCode}
                    </div>
                    <button
                      className="px-3 py-2 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors"
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(secretCode)
                        toast.success('Secret code copied to clipboard!')
                      }}
                    >
                      Copy
                    </button>
                  </div>
                  <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                    <p className="mb-1">To use this code:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Copy the code above (or click the Copy button)</li>
                      <li>In your authenticator app, choose "Manual entry" or "Enter setup key"</li>
                      <li>Paste the code and save</li>
                    </ol>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center text-red-600">
            Failed to generate QR code. Please try again.
          </div>
        )}

        <HeroInput
          isRequired
          control="text"
          label="Verification Code"
          maxLength={6}
          name="code"
          placeholder="Enter 6-digit code"
          radius="sm"
          size="md"
          validate={validateVerificationCode}
          variant="bordered"
        />

        <div className="flex justify-end gap-3 w-full">
          <Button
            disabled={isLoading || isInitializing}
            radius="sm"
            variant="flat"
            onPress={handleCancel}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={isLoading || isInitializing || !qrCodeUrl}
            isLoading={isLoading}
            radius="sm"
            type="submit"
          >
            {isLoading ? 'Verifying...' : 'Verify & Enable'}
          </Button>
        </div>
      </Form>
    </div>
  )
}
